import React from 'react';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Divider,
    Grid
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import TableColumnEditor from './TableColumnEditor';

const HeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
}));

const FieldsColumn = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const FieldConfigPanel = ({ field, onUpdate, onDelete, availableFields }) => {
    const handleChange = (key, value) => {
        onUpdate({ [key]: value });
    };

    return (
        <Box>
            <HeaderRow>
                <Typography variant="subtitle1" fontWeight={600}>
                    Field Properties
                </Typography>
                <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={onDelete}
                >
                    Delete
                </Button>
            </HeaderRow>

            <FieldsColumn>
                <FormControl size="small" fullWidth>
                    <InputLabel>Data Field</InputLabel>
                    <Select
                        value={field.fieldKey}
                        onChange={(e) => {
                            const def = availableFields.find(f => f.key === e.target.value);
                            handleChange('fieldKey', e.target.value);
                            if (def) {
                                onUpdate({ fieldKey: e.target.value, label: def.label, type: def.type });
                            }
                        }}
                        label="Data Field"
                    >
                        {availableFields.map(f => (
                            <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    size="small"
                    fullWidth
                    label="Label"
                    value={field.label || ''}
                    onChange={(e) => handleChange('label', e.target.value)}
                />

                <FormControl size="small" fullWidth>
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={field.type}
                        onChange={(e) => handleChange('type', e.target.value)}
                        label="Type"
                    >
                        <MenuItem value="text">Text</MenuItem>
                        <MenuItem value="date">Date</MenuItem>
                        <MenuItem value="currency">Currency</MenuItem>
                        <MenuItem value="multiline">Multiline</MenuItem>
                        <MenuItem value="table">Table</MenuItem>
                    </Select>
                </FormControl>

                <Divider />

                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Position & Size (PDF points)
                </Typography>

                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <TextField
                            size="small"
                            fullWidth
                            label="X"
                            type="number"
                            value={Math.round(field.x || 0)}
                            onChange={(e) => handleChange('x', Number(e.target.value))}
                            inputProps={{ step: 1 }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Y"
                            type="number"
                            value={Math.round(field.y || 0)}
                            onChange={(e) => handleChange('y', Number(e.target.value))}
                            inputProps={{ step: 1 }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Width"
                            type="number"
                            value={Math.round(field.width || 0)}
                            onChange={(e) => handleChange('width', Number(e.target.value))}
                            inputProps={{ step: 1, min: 10 }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Height"
                            type="number"
                            value={Math.round(field.height || 0)}
                            onChange={(e) => handleChange('height', Number(e.target.value))}
                            inputProps={{ step: 1, min: 10 }}
                        />
                    </Grid>
                </Grid>

                <Divider />

                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Typography
                </Typography>

                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Font Size"
                            type="number"
                            value={field.fontSize || 10}
                            onChange={(e) => handleChange('fontSize', Number(e.target.value))}
                            inputProps={{ step: 0.5, min: 4, max: 72 }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Weight</InputLabel>
                            <Select
                                value={field.fontWeight || 'normal'}
                                onChange={(e) => handleChange('fontWeight', e.target.value)}
                                label="Weight"
                            >
                                <MenuItem value="normal">Normal</MenuItem>
                                <MenuItem value="bold">Bold</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Alignment</InputLabel>
                            <Select
                                value={field.alignment || 'left'}
                                onChange={(e) => handleChange('alignment', e.target.value)}
                                label="Alignment"
                            >
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="center">Center</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Color"
                            type="color"
                            value={field.fontColor || '#000000'}
                            onChange={(e) => handleChange('fontColor', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>

                {field.type !== 'table' && (
                    <>
                        <Divider />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Prefix
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            label="Prefix Text"
                            value={field.prefix || ''}
                            onChange={(e) => handleChange('prefix', e.target.value)}
                            placeholder='e.g. "Invoice #"'
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Size"
                                    type="number"
                                    value={field.prefixFontSize ?? ''}
                                    onChange={(e) => handleChange('prefixFontSize', e.target.value === '' ? undefined : Number(e.target.value))}
                                    placeholder={String(field.fontSize || 10)}
                                    inputProps={{ step: 0.5, min: 4, max: 72 }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Weight</InputLabel>
                                    <Select
                                        value={field.prefixFontWeight || ''}
                                        onChange={(e) => handleChange('prefixFontWeight', e.target.value || undefined)}
                                        label="Weight"
                                        displayEmpty
                                    >
                                        <MenuItem value=""><em>Inherit</em></MenuItem>
                                        <MenuItem value="normal">Normal</MenuItem>
                                        <MenuItem value="bold">Bold</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Color"
                                    type="color"
                                    value={field.prefixFontColor || field.fontColor || '#000000'}
                                    onChange={(e) => handleChange('prefixFontColor', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>

                        <Divider />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Suffix
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            label="Suffix Text"
                            value={field.suffix || ''}
                            onChange={(e) => handleChange('suffix', e.target.value)}
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={4}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Size"
                                    type="number"
                                    value={field.suffixFontSize ?? ''}
                                    onChange={(e) => handleChange('suffixFontSize', e.target.value === '' ? undefined : Number(e.target.value))}
                                    placeholder={String(field.fontSize || 10)}
                                    inputProps={{ step: 0.5, min: 4, max: 72 }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Weight</InputLabel>
                                    <Select
                                        value={field.suffixFontWeight || ''}
                                        onChange={(e) => handleChange('suffixFontWeight', e.target.value || undefined)}
                                        label="Weight"
                                        displayEmpty
                                    >
                                        <MenuItem value=""><em>Inherit</em></MenuItem>
                                        <MenuItem value="normal">Normal</MenuItem>
                                        <MenuItem value="bold">Bold</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Color"
                                    type="color"
                                    value={field.suffixFontColor || field.fontColor || '#000000'}
                                    onChange={(e) => handleChange('suffixFontColor', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}

                {field.type === 'multiline' && (
                    <>
                        <TextField
                            size="small"
                            fullWidth
                            label="Line Spacing"
                            type="number"
                            value={field.lineSpacing || 14}
                            onChange={(e) => handleChange('lineSpacing', Number(e.target.value))}
                            inputProps={{ step: 1, min: 8 }}
                        />
                    </>
                )}

                {field.type === 'table' && (
                    <>
                        <Divider />
                        <TableColumnEditor
                            tableConfig={field.tableConfig || {}}
                            onUpdate={(tc) => handleChange('tableConfig', tc)}
                        />
                    </>
                )}
            </FieldsColumn>
        </Box>
    );
};

export default FieldConfigPanel;
